import { Employee, User } from "../types";
import BaseService from "./base-service";
import {
  AddEmployeeFirstLoginReq,
  ChangePasswordReq,
  EditEmployeeFirstLogin,
  EditEmployeeReq,
  GetUnverifiedUserReq,
  UpdateFirstLoginReq,
  VerifyEmployeeReq,
  VerifyManagerReq,
} from "./dto/request/user-request-dto";
import { RequestDto } from "./dto/request/request-dto";
import {
  AddedEmployee,
  GetEmployeeResponse,
  Manager,
  UnverifiedUser,
  VerifiedUser,
} from "./dto/response/user-response-dto";

interface EmployeesReqDTO {
  limit?: number;
  offset?: number;
  branchid?: number;
  groupid?: number;
}

export class UserService extends BaseService {
  private resource = "/api/users";
  private lastVerifyCountryCode: string | null = null;

  async getUser(
    params: null,
    onSuccess: (data: User) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(this.resource + "/me", params);
    await this.doGet<User>(reqParams, onSuccess, onError);
  }

  async getEmployees(
    params: EmployeesReqDTO,
    onSuccess: (data: GetEmployeeResponse) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(this.resource + "/employees", params);
    await this.doGet<GetEmployeeResponse>(reqParams, onSuccess, onError);
  }

  async addEmployees(
    params: AddEmployeeFirstLoginReq,
    onSuccess: (data: AddedEmployee[]) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(this.resource + "/employees", params);
    await this.doPost<AddedEmployee[]>(reqParams, onSuccess, onError);
  }

  async editEmployees(
    params: EditEmployeeReq,
    onSuccess: (data: Employee) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(this.resource + "/employees", params);
    await this.doPatch<Employee>(reqParams, onSuccess, onError);
  }

  async deleteEmployees(
    userId: number,
    onSuccess: (data: any) => void,
    onError: (errorMessage: string) => void,
    branchId?: number
  ) {
    const reqParams = new RequestDto(
      `${this.resource}/employees/${userId}${branchId ? `?branchId=${branchId}` : ""}`,
      {}
    );
    await this.doDelete<any>(reqParams, onSuccess, onError);
  }

  async getUnverifiedUser(
    params: GetUnverifiedUserReq,
    onSuccess: (data: UnverifiedUser) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto("/users/employees/verify", params);
    await this.doGet<UnverifiedUser>(
      reqParams,
      (data) => {
        const responseCountryCode = (data as any)?.countryCode ?? null;
        this.lastVerifyCountryCode = responseCountryCode;
        onSuccess(data);
      },
      onError
    );
  }

  async getUnverifiedManager(
    params: GetUnverifiedUserReq,
    onSuccess: (data: UnverifiedUser) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto("/users/managers/verify", params);
    await this.doGet<UnverifiedUser>(
      reqParams,
      (data) => {
        const responseCountryCode = (data as any)?.countryCode ?? null;
        this.lastVerifyCountryCode = responseCountryCode;
        onSuccess(data);
      },
      onError
    );
  }

  async verifyEmployee(
    params: VerifyEmployeeReq,
    onSuccess: (data: VerifiedUser) => void,
    onError: (errorMessage: string) => void
  ) {
    const formData = new FormData();
    formData.append("DocumentBack", params.DocumentBack);
    formData.append("DocumentFront", params.DocumentFront);
    if (params.PhotoImage) {
      formData.append("PhotoImage", params.PhotoImage);
    }
    formData.append("UserId", params.UserId.toString());
    formData.append("Token", params.Token);
    formData.append("BirthDate", params.BirthDate);
    formData.append("Phone", params.Phone);
    formData.append("Dni", params.Dni);
    formData.append("IdentityNumber", params.IdentityNumber);
    formData.append("IdentityType", params.IdentityType);
    formData.append("Password", params.Password);
    if (params.Address) {
      formData.append("Address", params.Address);
    }
    if (params.BankAccountNumber) {
      formData.append("BankAccountNumber", params.BankAccountNumber);
    }
    if (params.Alias) {
      formData.append("Alias", params.Alias);
    }
    if (params.BankId) {
      formData.append("BankId", params.BankId);
    }
    if (params.AccountTypeId) {
      formData.append("AccountTypeId", params.AccountTypeId);
    }
    const countryCode = (params as any)?.CountryCode ?? this.lastVerifyCountryCode;
    if (countryCode) {
      formData.append("CountryCode", countryCode);
    }

    const reqParams = new RequestDto("/users/employees/verify", formData, {
      "Content-Type": "multipart/form-data",
    });
    await this.doPatch<VerifiedUser>(reqParams, onSuccess, onError);
  }

  async verifyManager(
    params: VerifyManagerReq,
    onSuccess: (data: VerifiedUser) => void,
    onError: (errorMessage: string) => void
  ) {
    const formData = new FormData();
    formData.append("DocumentBack", params.DocumentBack);
    formData.append("DocumentFront", params.DocumentFront);
    formData.append("DocumentSelfie", params.DocumentSelfie);
    formData.append("Password", params.Password);
    formData.append("Token", params.Token);
    formData.append("UserId", params.UserId.toString());
    formData.append("FirstName", params.FirstName);
    formData.append("LastName", params.LastName);
    if (params.Address) formData.append("Address", params.Address);
    if (params.IdentityNumber) formData.append("IdentityNumber", params.IdentityNumber);
    if (params.Phone) formData.append("Phone", params.Phone);
    if (params.DepartmentId) formData.append("DepartmentId", params.DepartmentId.toString());
    if (params.CityId) formData.append("CityId", params.CityId.toString());
    if (params.CountryCode) formData.append("CountryCode", params.CountryCode.toString());

    const reqParams = new RequestDto("/users/managers/verify", formData, {
      "Content-Type": "multipart/form-data",
    });
    await this.doPatch<VerifiedUser>(reqParams, onSuccess, onError);
  }

  async sendRecoverEmail(
    params: { email: string },
    onSuccess: () => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto("/users/change_password", params);
    await this.doGet(reqParams, onSuccess, onError);
  }

  async verifyChangePassword(
    params: GetUnverifiedUserReq,
    onSuccess: (data: Manager) => void,
    onError: (erroMessage: string) => void
  ) {
    const reqParams = new RequestDto(
      "/users/employees/verifychangepassword",
      params
    );
    await this.doGet<Manager>(reqParams, onSuccess, onError);
  }

  async changePassword(
    params: ChangePasswordReq,
    onSuccess: (data: Manager) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto("/users/managers/change_password", params);
    await this.doPatch<Manager>(reqParams, onSuccess, onError);
  }

  async editEmployeeFirstLogin(
    params: EditEmployeeFirstLogin,
    onSuccess: (data: any) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(
      this.resource + "/employee/first-login",
      params
    );
    await this.doPatch<any>(reqParams, onSuccess, onError);
  }

  async updateFirstLogin(
    params: UpdateFirstLoginReq,
    onSuccess: (data: any) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(this.resource + "/firstlogin", params);
    await this.doPatch<any>(reqParams, onSuccess, onError);
  }

  async resendVerificationEmail(
    params: { userid: string },
    onSuccess: () => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(
      this.resource + "/employees/resend-verify",
      params
    );
    await this.doGet(reqParams, onSuccess, onError);
  }

  async uploadAvatar(
    params: {
      file: File;
      userId: number;
      branchId?: number;
    },
    onSuccess: (data: { imageUrl: string }) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(this.resource + "/avatar/upload", {
      userId: params.userId.toString(),
      ...(params.branchId != null ? { branchId: params.branchId.toString() } : {}),
    });
    await this.doUploadFile<{ imageUrl: string }>(
      reqParams,
      params.file,
      onSuccess,
      onError
    );
  }
  
  async addEmployeesBulk(
    params: AddEmployeeFirstLoginReq,
    onSuccess: (data: any) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(this.resource + "/employees/bulk", params);
    await this.doPost<any>(reqParams, onSuccess, onError);
  }

  async toggleEmployeeOnLeave(
    params: { UserId: number; BranchId: number },
    onSuccess: (data: any) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(
      this.resource + "/employees/toggle-on-leave",
      params
    );
    await this.doPatch<any>(reqParams, onSuccess, onError);
  }

  async setEmployeeQrDisplayConfig(
    params: { UserId: number; BranchId: number; QrDisplayConfigId: number },
    onSuccess: (data: { message: string }) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(
      this.resource + "/employees/qr-display-config",
      params
    );
    await this.doPatch<{ message: string }>(reqParams, onSuccess, onError);
  }

  async setEmployeesQrDisplayConfig(
    params: {
      QrDisplayConfigId: number;
      Items: { UserId: number; BranchId: number }[];
    },
    onSuccess: (data: { updated: number }) => void,
    onError: (errorMessage: string) => void
  ) {
    const reqParams = new RequestDto(
      this.resource + "/employees/qr-display-config",
      params
    );
    await this.doPut<{ updated: number }>(reqParams, onSuccess, onError);
  }
}
